import {
  getCurrentScope,
  onMounted,
  onScopeDispose,
  toValue,
  unref,
  type ComponentPublicInstance,
  type MaybeRef,
  type MaybeRefOrGetter,
} from 'vue'

export type ClickOutsideHandler = (event: MouseEvent) => void
export type ClickOutsideElement = HTMLElement | ComponentPublicInstance | string | null | undefined

export interface UseClickOutsideOptions {
  include?: MaybeRefOrGetter<ClickOutsideElement[]>
}

function resolveElements(value: ClickOutsideElement): HTMLElement[] {
  if (!value) return []
  if (typeof value === 'string') return [...document.querySelectorAll<HTMLElement>(value)]
  if (value instanceof HTMLElement) return [value]
  return value.$el instanceof HTMLElement ? [value.$el] : []
}

function eventHits(event: MouseEvent, elements: HTMLElement[]): boolean {
  const target = event.target
  const path = typeof event.composedPath === 'function' ? event.composedPath() : []
  return elements.some(
    (element) =>
      element === target ||
      (target instanceof Node && element.contains(target)) ||
      path.includes(element),
  )
}

/** Domain-free replacement for the legacy global click-outside directive. */
export function useClickOutside(
  target: MaybeRefOrGetter<ClickOutsideElement>,
  handler: MaybeRef<ClickOutsideHandler>,
  options: UseClickOutsideOptions = {},
): () => void {
  let listening = false

  const onClick = (event: MouseEvent): void => {
    if (eventHits(event, resolveElements(toValue(target)))) return
    const included = (toValue(options.include) ?? []).flatMap(resolveElements)
    if (eventHits(event, included)) return
    unref(handler)(event)
  }

  const cleanup = (): void => {
    if (!listening) return
    document.removeEventListener('click', onClick, true)
    listening = false
  }

  onMounted(() => {
    document.addEventListener('click', onClick, true)
    listening = true
  })
  if (getCurrentScope()) onScopeDispose(cleanup)

  return cleanup
}
